import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import blacklist from 'blacklist';

import stripDiacritics from './utils/stripDiacritics';

import Async from './async';
import Dropdown from './dropdown';
import Option from './option';
import OptionGroup from './option-group';
import Value from './value';
import SearchInput from './search-input';
import SearchResult from './search-result';
import SearchValue from './search-value';

function clone(obj) {
  const copy = {};
  for (let attr in obj) {
    if (obj.hasOwnProperty(attr)) {
      copy[attr] = obj[attr];
    }
  }
  return copy;
}

function stringifyValue(value) {
  if (typeof value === 'object') {
    return JSON.stringify(value);
  } else {
    return value;
  }
}

const stringOrNode = PropTypes.oneOfType([PropTypes.string, PropTypes.node]);

let instanceId = 1;

class Select extends React.Component {
  static displayName = 'Select';

  static propTypes = {
    addLabelText: PropTypes.string, // placeholder displayed when you want to add a label on a multi-value input
    allowCreate: PropTypes.bool, // whether to allow creation of new entries
    'aria-label': PropTypes.string, // Aria label (for assistive tech)
    'aria-labelledby': PropTypes.string, // HTML ID of an element that should be used as the label (for assistive tech)
    autoBlur: PropTypes.bool, // automatically blur the component when an option is selected
    autofocus: PropTypes.bool, // autofocus the component on mount
    autosize: PropTypes.bool, // whether to enable autosizing or not
    backspaceRemoves: PropTypes.bool, // whether backspace removes an item if there is no text input
    backspaceToRemoveMessage: PropTypes.string, // Message to use for screenreaders to press backspace to remove the current item -
    // {label} is replaced with the item label
    className: PropTypes.string, // className for the outer element
    clearAllText: stringOrNode, // title for the "clear" control when multi: true
    clearValueText: stringOrNode, // title for the "clear" control
    clearable: PropTypes.bool, // should it be possible to reset value
    delimiter: PropTypes.string, // delimiter to use to join multiple values for the hidden field value
    disabled: PropTypes.bool, // whether the Select is disabled or not
    dropdownComponent: PropTypes.func, // dropdown component to render the menu in
    escapeClearsValue: PropTypes.bool, // whether escape clears the value when the menu is closed
    filterOption: PropTypes.func, // method to filter a single option (option, filterString)
    filterOptions: PropTypes.any, // boolean to enable default filtering or function to filter the options array ([options], filterString, [values])
    ignoreAccents: PropTypes.bool, // whether to strip diacritics when filtering
    ignoreCase: PropTypes.bool, // whether to perform case-insensitive filtering
    inputProps: PropTypes.object, // custom attributes for the Input
    inputRenderer: PropTypes.func, // returns a custom input component
    isLoading: PropTypes.bool, // whether the Select is loading externally or not (such as options being loaded)
    isOpen: PropTypes.bool, // whether the Select dropdown menu is open or not
    joinValues: PropTypes.bool, // joins multiple values into a single form field with the delimiter (legacy mode)
    labelKey: PropTypes.string, // path of the label value in option objects
    matchPos: PropTypes.string, // (any|start) match the start or entire string when filtering
    matchProp: PropTypes.string, // (any|label|value) which option property to filter on
    menuBuffer: PropTypes.number, // optional buffer (in px) between the bottom of the viewport and the bottom of the menu
    menuContainerStyle: PropTypes.object, // optional style to apply to the menu container
    menuRenderer: PropTypes.func, // renders a custom menu with options
    menuStyle: PropTypes.object, // optional style to apply to the menu
    multi: PropTypes.bool, // multi-value input
    name: PropTypes.string, // generates a hidden <input /> tag with this field name for html forms
    newOptionCreator: PropTypes.func, // factory to create new options when allowCreate set
    noResultsText: stringOrNode, // placeholder displayed when there are no matching search results
    onBlur: PropTypes.func, // onBlur handler: function (event) {}
    onBlurResetsInput: PropTypes.bool, // whether input is cleared on blur
    onChange: PropTypes.func, // onChange handler: function (newValue) {}
    onClose: PropTypes.func, // fires when the menu is closed
    onFocus: PropTypes.func, // onFocus handler: function (event) {}
    onInputChange: PropTypes.func, // onInputChange handler: function (inputValue) {}
    onMenuScrollToBottom: PropTypes.func, // fires when the menu is scrolled to the bottom; can be used to paginate options
    onOpen: PropTypes.func, // fires when the menu is opened
    onValueClick: PropTypes.func, // onClick handler for value labels: function (value, event) {}
    openAfterFocus: PropTypes.bool, // boolean to enable opening dropdown when focused
    openOnFocus: PropTypes.bool, // always open options menu on focus
    optionClassName: PropTypes.string, // additional class(es) to apply to the <Option /> elements
    optionComponent: PropTypes.func, // option component to render in dropdown
    optionGroupComponent: PropTypes.func, // option group component to render in dropdown
    optionRenderer: PropTypes.func, // optionRenderer: function (option) {}
    options: PropTypes.array, // array of options
    optionsCount: PropTypes.number,
    searchCount: PropTypes.number,
    pageSize: PropTypes.number, // number of entries to page when using page up/down keys
    placeholder: stringOrNode, // field placeholder, displayed when there's no value
    searchPlaceholder: stringOrNode,
    renderInvalidValues: PropTypes.bool, // boolean to enable rendering values that do not match any options
    required: PropTypes.bool, // applies HTML5 required attribute when needed
    resetValue: PropTypes.any, // value to use when you clear the control
    scrollMenuIntoView: PropTypes.bool, // boolean to enable the viewport to shift so that the full menu fully visible when engaged
    searchable: PropTypes.bool, // whether to enable searching feature or not
    simpleValue: PropTypes.bool, // pass the value to onChange as a simple value (legacy pre 1.0 mode), defaults to false
    style: PropTypes.object, // optional style to apply to the control
    tabIndex: PropTypes.string, // optional tab index of the control
    tabSelectsValue: PropTypes.bool, // whether to treat tabbing out while focused to be value selection
    value: PropTypes.any, // initial field value
    valueComponent: PropTypes.func, // value component to render
    valueKey: PropTypes.string, // path of the label value in option objects
    valueRenderer: PropTypes.func, // valueRenderer: function (option) {}
    wrapperStyle: PropTypes.object // optional style to apply to the component wrapper
  };

  static Async = Async;

  static defaultProps = {
    addLabelText: 'Add "{label}"?',
    autosize: true,
    allowCreate: false,
    backspaceRemoves: true,
    backspaceToRemoveMessage: 'Press backspace to remove {label}',
    clearable: true,
    clearAllText: 'Clear all',
    clearValueText: 'Clear value',
    delimiter: ',',
    disabled: false,
    dropdownComponent: Dropdown,
    escapeClearsValue: true,
    filterOptions: true,
    ignoreAccents: true,
    ignoreCase: true,
    inputProps: {},
    isLoading: false,
    joinValues: false,
    labelKey: 'label',
    matchPos: 'any',
    matchProp: 'any',
    menuBuffer: 0,
    multi: false,
    noResultsText: 'No results found',
    onBlurResetsInput: true,
    openAfterFocus: false,
    optionComponent: Option,
    optionGroupComponent: OptionGroup,
    pageSize: 5,
    placeholder: 'Select...',
    renderInvalidValues: false,
    required: false,
    resetValue: null,
    scrollMenuIntoView: true,
    searchable: true,
    simpleValue: false,
    tabSelectsValue: true,
    valueComponent: Value,
    valueKey: 'value'
  };

  state = {
    inputValue: '',
    isFocused: false,
    isLoading: false,
    isOpen: false,
    isPseudoFocused: false,
    required: false
  };

  UNSAFE_componentWillMount() {
    this._flatOptions = this.flattenOptions(this.props.options);
    this._instancePrefix = 'react-select-' + ++instanceId + '-';
    const valueArray = this.getValueArray(this.props.value);

    if (this.props.required) {
      this.setState({
        required: this.handleRequired(valueArray[0], this.props.multi)
      });
    }
  }

  componentDidMount() {
    if (this.props.autofocus) {
      this.focus();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.options !== this.props.options) {
      this._flatOptions = this.flattenOptions(nextProps.options);
    }

    const valueArray = this.getValueArray(nextProps.value, nextProps);

    if (nextProps.required) {
      this.setState({
        required: this.handleRequired(valueArray[0], nextProps.multi)
      });
    }
  }

  UNSAFE_componentWillUpdate(nextProps, nextState) {
    if (nextState.isOpen !== this.state.isOpen) {
      const handler = nextState.isOpen ? nextProps.onOpen : nextProps.onClose;
      handler && handler();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // focus to the selected option
    if (
      this.refs.menu &&
      this.refs.focused &&
      this.state.isOpen &&
      !this.hasScrolledToOption
    ) {
      let focusedOptionNode = ReactDOM.findDOMNode(this.refs.focused);
      let focusedOptionPreviousSibling = focusedOptionNode.previousSibling;
      let focusedOptionParent = focusedOptionNode.parentElement;
      let menuNode = ReactDOM.findDOMNode(this.refs.menu);
      if (focusedOptionPreviousSibling) {
        menuNode.scrollTop = focusedOptionPreviousSibling.offsetTop;
      } else if (focusedOptionParent && focusedOptionParent === 'Select-menu') {
        menuNode.scrollTop = focusedOptionParent.offsetTop;
      } else {
        menuNode.scrollTop = focusedOptionNode.offsetTop;
      }
      this.hasScrolledToOption = true;
    } else if (!this.state.isOpen) {
      this.hasScrolledToOption = false;
    }

    if (
      this._scrollToFocusedOptionOnUpdate &&
      this.refs.focused &&
      this.refs.menu
    ) {
      this._scrollToFocusedOptionOnUpdate = false;
      var focusedDOM = ReactDOM.findDOMNode(this.refs.focused);
      var menuDOM = ReactDOM.findDOMNode(this.refs.menu);
      var focusedRect = focusedDOM.getBoundingClientRect();
      var menuRect = menuDOM.getBoundingClientRect();
      if (
        focusedRect.bottom > menuRect.bottom ||
        focusedRect.top < menuRect.top
      ) {
        menuDOM.scrollTop =
          focusedDOM.offsetTop + focusedDOM.clientHeight - menuDOM.offsetHeight;
      }
    }
    if (this.props.scrollMenuIntoView && this.refs.menuContainer) {
      var menuContainerRect = this.refs.menuContainer.getBoundingClientRect();
      if (
        window.innerHeight <
        menuContainerRect.bottom + this.props.menuBuffer
      ) {
        window.scrollBy(
          0,
          menuContainerRect.bottom + this.props.menuBuffer - window.innerHeight
        );
      }
    }
    if (prevProps.disabled !== this.props.disabled) {
      this.setState({ isFocused: false }); // eslint-disable-line react/no-did-update-set-state
      this.closeMenu();
    }
  }

  focus = () => {
    if (!this.refs.input) return;
    this.refs.input.focus();

    if (this.props.openAfterFocus) {
      this.setState({
        isOpen: true
      });
    }
  };

  blurInput = () => {
    if (!this.refs.input) return;
    this.refs.input.blur();
  };

  handleTouchMove = event => {
    // Set a flag that the view is being dragged
    this.dragging = true;
  };

  handleTouchStart = event => {
    // Set a flag that the view is not being dragged
    this.dragging = false;
  };

  handleTouchEnd = event => {
    // Check if the view is being dragged, In this case
    // we don't want to fire the click event (because the user only wants to scroll)
    if (this.dragging) return;

    // Fire the mouse events
    this.handleMouseDown(event);
  };

  handleTouchEndClearValue = event => {
    // Check if the view is being dragged, In this case
    // we don't want to fire the click event (because the user only wants to scroll)
    if (this.dragging) return;

    // Clear the value
    this.clearValue(event);
  };

  handleMouseDown = event => {
    // if the event was triggered by a mousedown and not the primary
    // button, or if the component is disabled, ignore it.
    if (
      this.props.disabled ||
      (event.type === 'mousedown' && event.button !== 0)
    ) {
      return;
    }

    if (event.target.tagName === 'INPUT') {
      return;
    }

    // prevent default event handlers
    event.stopPropagation();
    event.preventDefault();

    // for the non-searchable select, toggle the menu
    if (!this.props.searchable) {
      this.focus();
      return this.setState({
        isOpen: !this.state.isOpen
      });
    }

    if (this.state.isFocused) {
      // On iOS, we can get into a state where we think the input is focused but it isn't really,
      // since iOS ignores programmatic calls to input.focus() that weren't triggered by a click event.
      // Call focus() again here to be safe.
      this.focus();

      let input = this.refs.input;
      if (typeof input.getInput === 'function') {
        // Get the actual DOM input if the ref is an <Input /> component
        input = input.getInput();
      }

      // clears the value so that the cursor will be at the end of input when the component re-renders
      input.value = '';

      // if the input is focused, ensure the menu is open
      this.setState({
        isOpen: true,
        isPseudoFocused: false
      });
    } else {
      // otherwise, focus the input and open the menu
      this._openAfterFocus = true;
      this.focus();
    }
  };

  handleMouseDownOnArrow = event => {
    // if the event was triggered by a mousedown and not the primary
    // button, or if the component is disabled, ignore it.
    if (
      this.props.disabled ||
      (event.type === 'mousedown' && event.button !== 0)
    ) {
      return;
    }
    // If the menu isn't open, let the event bubble to the main handleMouseDown
    if (!this.state.isOpen) {
      return;
    }
    // prevent default event handlers
    event.stopPropagation();
    event.preventDefault();
    // close the menu
    this.closeMenu();
  };

  handleMouseDownOnMenu = event => {
    // if the event was triggered by a mousedown and not the primary
    // button, or if the component is disabled, ignore it.
    if (
      this.props.disabled ||
      (event.type === 'mousedown' && event.button !== 0)
    ) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();

    this._openAfterFocus = true;
    this.focus();
  };

  closeMenu = () => {
    this.setState({
      isOpen: false,
      isPseudoFocused: this.state.isFocused && !this.props.multi,
      inputValue: ''
    });
    this.hasScrolledToOption = false;
  };

  handleInputFocus = event => {
    var isOpen =
      this.state.isOpen ||
      this._openAfterFocus ||
      this.props.openOnFocus ||
      this.props.searchable;
    if (this.props.onFocus) {
      this.props.onFocus(event);
    }
    this.setState({
      isFocused: true,
      isOpen: isOpen
    });
    this._openAfterFocus = false;
  };

  handleInputBlur = event => {
    // The check for menu.contains(activeElement) is necessary to prevent IE11's scrollbar from closing the menu in certain contexts.
    if (
      this.refs.menu &&
      (this.refs.menu === document.activeElement ||
        this.refs.menu.contains(document.activeElement))
    ) {
      this.focus();
      return;
    }

    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
    var onBlurredState = {
      isFocused: false,
      isOpen: false,
      isPseudoFocused: false
    };
    if (this.props.onBlurResetsInput) {
      onBlurredState.inputValue = '';
    }
    this.setState(onBlurredState);
  };

  handleInputChange = event => {
    let newInputValue = event.target.value;
    if (
      this.state.inputValue !== event.target.value &&
      this.props.onInputChange
    ) {
      let nextState = this.props.onInputChange(newInputValue);
      // Note: != used deliberately here to catch undefined and null
      if (nextState != null && typeof nextState !== 'object') {
        newInputValue = '' + nextState;
      }
    }
    this.setState({
      isOpen: true,
      isPseudoFocused: false,
      inputValue: newInputValue
    });
  };

  handleKeyDown = event => {
    if (this.props.disabled) return;
    switch (event.keyCode) {
      case 8: // backspace
        if (!this.state.inputValue && this.props.backspaceRemoves) {
          event.preventDefault();
          this.popValue();
        }
        return;
      case 9: // tab
        if (
          event.shiftKey ||
          !this.state.isOpen ||
          !this.props.tabSelectsValue
        ) {
          return;
        }
        this.selectFocusedOption();
        return;
      case 13: // enter
        if (!this.state.isOpen) return;
        event.stopPropagation();
        this.selectFocusedOption();
        break;
      case 27: // escape
        if (this.state.isOpen) {
          this.closeMenu();
          event.stopPropagation();
        } else if (this.props.clearable && this.props.escapeClearsValue) {
          this.clearValue(event);
          event.stopPropagation();
        }
        break;
      case 38: // up
        this.focusPreviousOption();
        break;
      case 40: // down
        this.focusNextOption();
        break;
      case 33: // page up
        this.focusPageUpOption();
        break;
      case 34: // page down
        this.focusPageDownOption();
        break;
      case 35: // end key
        this.focusEndOption();
        break;
      case 36: // home key
        this.focusStartOption();
        break;
      // case 188: // ,
      // 	if (this.props.allowCreate && this.props.multi) {
      // 		event.preventDefault();
      // 		event.stopPropagation();
      // 		this.selectFocusedOption();
      // 	} else {
      // 		return;
      // 	}
      // break;
      default:
        return;
    }
    event.preventDefault();
  };

  handleValueClick = (option, event) => {
    if (!this.props.onValueClick) return;
    this.props.onValueClick(option, event);
  };

  handleMenuScroll = event => {
    if (!this.props.onMenuScrollToBottom) return;
    let { target } = event;
    if (
      target.scrollHeight > target.offsetHeight &&
      !(target.scrollHeight - target.offsetHeight - target.scrollTop)
    ) {
      this.props.onMenuScrollToBottom();
    }
  };

  handleRequired = (value, multi) => {
    if (!value) return true;
    return multi ? value.length === 0 : Object.keys(value).length === 0;
  };

  getOptionLabel = op => {
    return op[this.props.labelKey];
  };

  /**
   * Turns a value into an array from the given options
   * @param	{String|Number|Array}	value		- the value of the select input
   * @param	{Object}		nextProps	- optionally specify the nextProps so the returned array uses the latest configuration
   * @returns	{Array}	the value of the select represented in an array
   */
  getValueArray = (value, nextProps) => {
    /** support optionally passing in the `nextProps` so `componentWillReceiveProps` updates will function as expected */
    const props = typeof nextProps === 'object' ? nextProps : this.props;
    if (props.multi) {
      if (typeof value === 'string') value = value.split(props.delimiter);
      if (!Array.isArray(value)) {
        if (value === null || value === undefined) return [];
        value = [value];
      }
      return value.map(value => this.expandValue(value, props)).filter(i => i);
    }
    var expandedValue = this.expandValue(value, props);
    return expandedValue ? [expandedValue] : [];
  };

  /**
   * Retrieve a value from the given options and valueKey
   * @param	{String|Number|Array}	value	- the selected value(s)
   * @param	{Object}		props	- the Select component's props (or nextProps)
   */
  expandValue = (value, props) => {
    if (typeof value !== 'string' && typeof value !== 'number') return value;
    let { labelKey, valueKey, renderInvalidValues } = this.props;
    let options = this._flatOptions;
    if (!options || value === '') return;
    for (var i = 0; i < options.length; i++) {
      if (options[i][valueKey] === value) return options[i];
    }

    // no matching option, return an invalid option if renderInvalidValues is enabled
    if (renderInvalidValues) {
      return {
        invalid: true,
        [labelKey]: value,
        [valueKey]: value
      };
    }
  };

  setValue = value => {
    if (this.props.autoBlur) {
      this.blurInput();
    }
    if (!this.props.onChange) return;
    if (this.props.required) {
      const required = this.handleRequired(value, this.props.multi);
      this.setState({ required });
    }
    if (this.props.simpleValue && value) {
      value = this.props.multi
        ? value.map(i => i[this.props.valueKey]).join(this.props.delimiter)
        : value[this.props.valueKey];
    }
    this.props.onChange(value);
  };

  selectValue = value => {
    //NOTE: update value in the callback to make sure the input value is empty so that there are no sttyling issues (Chrome had issue otherwise)
    this.hasScrolledToOption = false;
    if (this.props.multi) {
      this.setState(
        {
          inputValue: '',
          focusedIndex: null
        },
        () => {
          this.addValue(value);
        }
      );
    } else {
      this.setState(
        {
          isOpen: false,
          inputValue: '',
          isPseudoFocused: this.state.isFocused
        },
        () => {
          this.setValue(value);
        }
      );
    }
  };

  addValue = value => {
    var valueArray = this.getValueArray(this.props.value);
    this.setValue(valueArray.concat(value));
  };

  popValue = () => {
    var valueArray = this.getValueArray(this.props.value);
    if (!valueArray.length) return;
    if (valueArray[valueArray.length - 1].clearableValue === false) return;
    this.setValue(valueArray.slice(0, valueArray.length - 1));
  };

  removeValue = value => {
    var valueArray = this.getValueArray(this.props.value);
    this.setValue(valueArray.filter(i => i !== value));
    this.focus();
  };

  clearValue = event => {
    // if the event was triggered by a mousedown and not the primary
    // button, ignore it.
    if (event && event.type === 'mousedown' && event.button !== 0) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    this.setValue(this.props.resetValue);
    this.setState(
      {
        isOpen: false,
        inputValue: ''
      },
      this.focus
    );

    if (this.props.onChange) {
      this.props.onChange(null);
    }
  };

  focusOption = option => {
    this.setState({
      focusedOption: option
    });
  };

  focusNextOption = () => {
    this.focusAdjacentOption('next');
  };

  focusPreviousOption = () => {
    this.focusAdjacentOption('previous');
  };

  focusPageUpOption = () => {
    this.focusAdjacentOption('page_up');
  };

  focusPageDownOption = () => {
    this.focusAdjacentOption('page_down');
  };

  focusStartOption = () => {
    this.focusAdjacentOption('start');
  };

  focusEndOption = () => {
    this.focusAdjacentOption('end');
  };

  focusAdjacentOption = dir => {
    var options = this._visibleOptions
      .map((option, index) => ({ option, index }))
      .filter(option => !option.option.disabled);
    this._scrollToFocusedOptionOnUpdate = true;
    if (!this.state.isOpen) {
      this.setState({
        isOpen: true,
        inputValue: '',
        focusedOption:
          this._focusedOption ||
          options[dir === 'next' ? 0 : options.length - 1].option
      });
      return;
    }
    if (!options.length) return;
    var focusedIndex = -1;
    for (var i = 0; i < options.length; i++) {
      if (this._focusedOption === options[i].option) {
        focusedIndex = i;
        break;
      }
    }
    if (dir === 'next' && focusedIndex !== -1) {
      focusedIndex = (focusedIndex + 1) % options.length;
    } else if (dir === 'previous') {
      if (focusedIndex > 0) {
        focusedIndex = focusedIndex - 1;
      } else {
        focusedIndex = options.length - 1;
      }
    } else if (dir === 'start') {
      focusedIndex = 0;
    } else if (dir === 'end') {
      focusedIndex = options.length - 1;
    } else if (dir === 'page_up') {
      var potentialIndex = focusedIndex - this.props.pageSize;
      if (potentialIndex < 0) {
        focusedIndex = 0;
      } else {
        focusedIndex = potentialIndex;
      }
    } else if (dir === 'page_down') {
      var potentialIndex = focusedIndex + this.props.pageSize;
      if (potentialIndex > options.length - 1) {
        focusedIndex = options.length - 1;
      } else {
        focusedIndex = potentialIndex;
      }
    }

    if (focusedIndex === -1) {
      focusedIndex = 0;
    }

    this.setState({
      focusedIndex: options[focusedIndex].index,
      focusedOption: options[focusedIndex].option
    });
  };

  selectFocusedOption = () => {
    if (this._focusedOption) {
      return this.selectValue(this._focusedOption);
    }
  };

  renderLoading = () => {
    if (!this.props.isLoading) return;
    return (
      <span className="Select-loading-zone" aria-hidden="true">
        <span className="Select-loading" />
      </span>
    );
  };

  renderValue = (valueArray, isOpen) => {
    let renderLabel = this.props.valueRenderer || this.getOptionLabel;
    let ValueComponent = this.props.valueComponent;

    if (this.props.searchable && isOpen) {
      return null;
    }

    if (!valueArray.length) {
      return <div className="Select-placeholder">{this.props.placeholder}</div>;
    }

    let onClick = this.props.onValueClick ? this.handleValueClick : null;
    if (this.props.multi) {
      return valueArray.map((value, i) => {
        return (
          <ValueComponent
            id={this._instancePrefix + '-value-' + i}
            instancePrefix={this._instancePrefix}
            disabled={this.props.disabled || value.clearableValue === false}
            key={`value-${i}-${value[this.props.valueKey]}`}
            onClick={onClick}
            onRemove={this.removeValue}
            value={value}
          >
            {renderLabel(value)}
            <span className="Select-aria-only">&nbsp;</span>
          </ValueComponent>
        );
      });
    } else if (!this.state.inputValue) {
      if (isOpen) onClick = null;
      return (
        <ValueComponent
          id={this._instancePrefix + '-value-item'}
          disabled={this.props.disabled}
          instancePrefix={this._instancePrefix}
          onClick={onClick}
          value={valueArray[0]}
        >
          {renderLabel(valueArray[0])}
        </ValueComponent>
      );
    }
  };

  renderInput = (valueArray, focusedOptionIndex) => {
    if (this.props.inputRenderer) {
      return this.props.inputRenderer();
    } else {
      var className = classNames(
        'Select-input',
        this.props.inputProps.className
      );
      const isOpen = !!this.state.isOpen;

      const ariaOwns = classNames({
        [this._instancePrefix + '-list']: isOpen,
        [this._instancePrefix + '-backspace-remove-message']:
          this.props.multi &&
          !this.props.disabled &&
          this.state.isFocused &&
          !this.state.inputValue
      });

      // TODO: Check how this project includes Object.assign()
      const inputProps = Object.assign({}, this.props.inputProps, {
        role: 'combobox',
        'aria-expanded': '' + isOpen,
        'aria-owns': ariaOwns,
        'aria-haspopup': '' + isOpen,
        'aria-activedescendant': isOpen
          ? this._instancePrefix + '-option-' + focusedOptionIndex
          : this._instancePrefix + '-value',
        'aria-labelledby': this.props['aria-labelledby'],
        'aria-label': this.props['aria-label'],
        className: className,
        tabIndex: this.props.tabIndex,
        onBlur: this.handleInputBlur,
        onChange: this.handleInputChange,
        onFocus: this.handleInputFocus,
        ref: 'input',
        required: this.state.required,
        value: this.state.inputValue
      });

      if (this.props.disabled || !this.props.searchable) {
        const divProps = blacklist(this.props.inputProps, 'inputClassName');
        return (
          <div
            {...divProps}
            role="combobox"
            aria-expanded={isOpen}
            aria-owns={
              isOpen
                ? this._instancePrefix + '-list'
                : this._instancePrefix + '-value'
            }
            aria-activedescendant={
              isOpen
                ? this._instancePrefix + '-option-' + focusedOptionIndex
                : this._instancePrefix + '-value'
            }
            className={className}
            tabIndex={this.props.tabIndex || 0}
            onBlur={this.handleInputBlur}
            onFocus={this.handleInputFocus}
            ref="input"
            aria-readonly={'' + !!this.props.disabled}
            style={{ border: 0, width: 1, display: 'inline-block' }}
          />
        );
      }

      return (
        <SearchInput
          placeholder={this.props.searchPlaceholder}
          editable={isOpen}
          {...inputProps}
        />
      );
    }
  };

  renderClear = () => {
    if (
      !this.props.clearable ||
      !this.props.value ||
      (this.props.multi && !this.props.value.length) ||
      this.props.disabled ||
      this.props.isLoading
    )
      return;
    return (
      <span
        className="Select-clear-zone"
        title={
          this.props.multi ? this.props.clearAllText : this.props.clearValueText
        }
        aria-label={
          this.props.multi ? this.props.clearAllText : this.props.clearValueText
        }
        onMouseDown={this.clearValue}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
        onTouchEnd={this.handleTouchEndClearValue}
      >
        <span
          className="Select-clear"
          dangerouslySetInnerHTML={{ __html: '&times;' }}
        />
      </span>
    );
  };

  renderArrow = () => {
    if (this.props.arrowRenderer) {
      return (
        <span className="Select-arrow-zone">
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignContent: 'center',
              height: '100%'
            }}
          >
            {this.props.arrowRenderer()}
          </span>
        </span>
      );
    }
    return (
      <span
        className="Select-arrow-zone"
        onMouseDown={this.handleMouseDownOnArrow}
      >
        <span
          className="Select-arrow"
          onMouseDown={this.handleMouseDownOnArrow}
        />
      </span>
    );
  };

  filterOptions = (options, excludeOptions) => {
    let excludeOptionValues = null;
    var filterValue = this.state.inputValue;
    if (typeof this.props.filterOptions === 'function') {
      return this.props.filterOptions.call(
        this,
        options,
        filterValue,
        excludeOptions
      );
    } else if (this.props.filterOptions) {
      if (this.props.ignoreAccents) {
        filterValue = stripDiacritics(filterValue);
      }
      if (this.props.ignoreCase) {
        filterValue = filterValue.toLowerCase();
      }
      if (excludeOptions)
        excludeOptionValues = excludeOptions.map(i => i[this.props.valueKey]);
      const includeOption = option => {
        if (
          excludeOptionValues &&
          excludeOptionValues.indexOf(option[this.props.valueKey]) > -1
        )
          return false;
        if (this.props.filterOption)
          return this.props.filterOption.call(this, option, filterValue);
        if (!filterValue) return true;
        var valueTest = String(option[this.props.valueKey]);
        var labelTest = String(option[this.props.labelKey]);
        if (this.props.ignoreAccents) {
          if (this.props.matchProp !== 'label')
            valueTest = stripDiacritics(valueTest);
          if (this.props.matchProp !== 'value')
            labelTest = stripDiacritics(labelTest);
        }
        if (this.props.ignoreCase) {
          if (this.props.matchProp !== 'label')
            valueTest = valueTest.toLowerCase();
          if (this.props.matchProp !== 'value')
            labelTest = labelTest.toLowerCase();
        }
        return this.props.matchPos === 'start'
          ? (this.props.matchProp !== 'label' &&
              valueTest.substr(0, filterValue.length) === filterValue) ||
              (this.props.matchProp !== 'value' &&
                labelTest.substr(0, filterValue.length) === filterValue)
          : (this.props.matchProp !== 'label' &&
              valueTest.indexOf(filterValue) >= 0) ||
              (this.props.matchProp !== 'value' &&
                labelTest.indexOf(filterValue) >= 0);
      };
      let filteredOptions = [];
      options.forEach(option => {
        if (this.isGroup(option)) {
          const filteredGroup = clone(option);
          filteredGroup.options = this.filterOptions(
            option.options,
            excludeOptions
          );
          if (filteredGroup.options.length) {
            filteredOptions.push(filteredGroup);
          }
        } else if (includeOption(option)) {
          filteredOptions.push(option);
        }
      });
      return filteredOptions;
    } else {
      return options;
    }
  };

  isGroup = option => {
    return option && Array.isArray(option.options);
  };

  flattenOptions = options => {
    if (!options) return;
    let flatOptions = [];
    for (let i = 0; i < options.length; i++) {
      if (this.isGroup(options[i])) {
        flatOptions = flatOptions.concat(
          this.flattenOptions(options[i].options)
        );
      } else {
        flatOptions.push(options[i]);
      }
    }
    return flatOptions;
  };

  renderMenu = (options, valueArray, focusedOption) => {
    if (options && options.length) {
      if (this.props.menuRenderer) {
        return this.props.menuRenderer({
          focusedOption,
          focusOption: this.focusOption,
          labelKey: this.props.labelKey,
          options,
          selectValue: this.selectValue,
          valueArray
        });
      } else {
        let OptionGroup = this.props.optionGroupComponent;
        let Option = this.props.optionComponent;
        let renderLabel = this.props.optionRenderer || this.getOptionLabel;

        return options.map((option, i) => {
          if (this.isGroup(option)) {
            let optionGroupClass = classNames({
              'Select-option-group': true
            });

            return (
              <OptionGroup
                className={optionGroupClass}
                key={`option-group-${i}`}
                label={renderLabel(option)}
                option={option}
              >
                {this.renderMenu(option.options, valueArray, focusedOption)}
              </OptionGroup>
            );
          } else {
            let isSelected = valueArray && valueArray.indexOf(option) > -1;
            let isFocused = option === focusedOption;
            let optionRef = isFocused ? 'focused' : null;
            let optionClass = classNames(this.props.optionClassName, {
              'Select-option': true,
              'is-selected': isSelected,
              'is-focused': isFocused,
              'is-disabled': option.disabled
            });

            return (
              <Option
                instancePrefix={this._instancePrefix}
                optionIndex={i}
                className={optionClass}
                isDisabled={option.disabled}
                isFocused={isFocused}
                key={`option-${i}-${option[this.props.valueKey]}`}
                onSelect={this.selectValue}
                onFocus={this.focusOption}
                option={option}
                isSelected={isSelected}
                ref={optionRef}
              >
                {renderLabel(option)}
              </Option>
            );
          }
        });
      }
    } else if (this.props.noResultsText) {
      return <div className="Select-noresults">{this.props.noResultsText}</div>;
    } else {
      return null;
    }
  };

  renderHiddenField = valueArray => {
    if (!this.props.name) return;
    if (this.props.joinValues) {
      let value = valueArray
        .map(i => stringifyValue(i[this.props.valueKey]))
        .join(this.props.delimiter);
      return (
        <input
          type="hidden"
          ref="value"
          name={this.props.name}
          value={value}
          disabled={this.props.disabled}
        />
      );
    }
    return valueArray.map((item, index) => (
      <input
        key={'hidden.' + index}
        type="hidden"
        ref={'value' + index}
        name={this.props.name}
        value={stringifyValue(item[this.props.valueKey])}
        disabled={this.props.disabled}
      />
    ));
  };

  getFocusableOptionIndex = selectedOption => {
    var options = this._visibleOptions;
    if (!options.length) return null;

    let focusedOption = this.state.focusedOption || selectedOption;
    if (focusedOption && !focusedOption.disabled) {
      const focusedOptionIndex = options.indexOf(focusedOption);
      if (focusedOptionIndex !== -1) {
        return focusedOptionIndex;
      }
    }

    for (var i = 0; i < options.length; i++) {
      if (!options[i].disabled) return i;
    }
    return null;
  };

  renderOuter = (options, valueArray, focusedOption) => {
    let Dropdown = this.props.dropdownComponent;
    let menu = this.renderMenu(options, valueArray, focusedOption);

    return (
      <Dropdown>
        <div
          ref="menuContainer"
          className="Select-menu-outer"
          style={this.props.menuContainerStyle}
        >
          {!this.props.searchable ? null : (
            <SearchValue
              multi={this.props.multi}
              value={valueArray}
              onClear={this.clearValue}
            />
          )}
          <div
            ref="menu"
            role="listbox"
            className="Select-menu"
            id={this._instancePrefix + '-list'}
            style={this.props.menuStyle}
            onScroll={this.handleMenuScroll}
            onMouseDown={this.handleMouseDownOnMenu}
          >
            {menu}
          </div>
          {this.props.searchable ? (
            <SearchResult
              optionsCount={this.props.optionsCount}
              searchCount={this.props.searchCount}
              empty={!menu}
            />
          ) : null}
        </div>
      </Dropdown>
    );
  };

  render() {
    let valueArray = this.getValueArray(this.props.value);
    let options = this.filterOptions(
      this.props.options || [],
      this.props.multi ? valueArray : null
    );
    this._visibleOptions = this.flattenOptions(options);
    let isOpen =
      typeof this.props.isOpen === 'boolean'
        ? this.props.isOpen
        : this.state.isOpen;
    if (
      this.props.multi &&
      !options.length &&
      valueArray.length &&
      !this.state.inputValue
    )
      isOpen = false;
    const focusedOptionIndex = this.getFocusableOptionIndex(valueArray[0]);

    let focusedOption = null;
    if (focusedOptionIndex !== null) {
      focusedOption = this._focusedOption = this._visibleOptions[
        focusedOptionIndex
      ];
    } else {
      focusedOption = this._focusedOption = null;
    }
    let className = classNames('Select', this.props.className, {
      'Select--multi': this.props.multi,
      'Select--single': !this.props.multi,
      'is-disabled': this.props.disabled,
      'is-focused': this.state.isFocused,
      'is-loading': this.props.isLoading,
      'is-open': isOpen,
      'is-pseudo-focused': this.state.isPseudoFocused,
      'is-searchable': this.props.searchable,
      'has-value': valueArray.length
    });

    let removeMessage = null;
    if (
      this.props.multi &&
      !this.props.disabled &&
      valueArray.length &&
      !this.state.inputValue &&
      this.state.isFocused &&
      this.props.backspaceRemoves
    ) {
      removeMessage = (
        <span
          id={this._instancePrefix + '-backspace-remove-message'}
          className="Select-aria-only"
          aria-live="assertive"
        >
          {this.props.backspaceToRemoveMessage.replace(
            '{label}',
            valueArray[valueArray.length - 1][this.props.labelKey]
          )}
        </span>
      );
    }

    const hasSearchValue =
      this.props.searchable && isOpen && valueArray.length > 0;

    return (
      <div ref="wrapper" className={className} style={this.props.wrapperStyle}>
        {this.renderHiddenField(valueArray)}
        <div
          ref="control"
          className={classNames('Select-control', {
            noBottomBorder: hasSearchValue
          })}
          style={this.props.style}
          onKeyDown={this.handleKeyDown}
          onMouseDown={this.handleMouseDown}
          onTouchEnd={this.handleTouchEnd}
          onTouchStart={this.handleTouchStart}
          onTouchMove={this.handleTouchMove}
        >
          <span
            className="Select-multi-value-wrapper"
            id={this._instancePrefix + '-value'}
          >
            {this.renderInput(valueArray, focusedOptionIndex)}
            {this.renderValue(valueArray, isOpen)}
          </span>
          {removeMessage}
          {this.renderLoading()}
          {this.renderClear()}
          {this.renderArrow()}
        </div>
        {isOpen
          ? this.renderOuter(
              options,
              !this.props.multi ? valueArray : null,
              focusedOption
            )
          : null}
      </div>
    );
  }
}

export default Select;
